import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '@/lib/apiClient';
import { Pencil, Save, Power, PowerOff, User, Plus, Upload, Download, Users, X } from 'lucide-react';
import * as XLSX from 'xlsx';

export default function CompanyEmployees() {
    const [employees, setEmployees] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [editingEmployee, setEditingEmployee] = useState(null);
    const [editFormData, setEditFormData] = useState({
        full_name: '', personnel_code: '', national_code: '', mobile_phone: '', department: '', is_active: true
    });
    const [savingEdit, setSavingEdit] = useState(false);
    const [togglingId, setTogglingId] = useState(null);
    const [editError, setEditError] = useState('');

    const navigate = useNavigate();

    const loadEmployees = () => {
        apiClient.GET('/api/v1/company/employees').then(res => {
            if (res.data) setEmployees(res.data);
        });
    };

    useEffect(() => {
        loadEmployees();
    }, []);

    // === توابع اکسل ===
    const downloadTemplate = () => {
        const ws = XLSX.utils.json_to_sheet([{
            'نام و نام خانوادگی': 'علی رضایی',
            'کد پرسنلی': '1001',
            'کد ملی': '0012345678',
            'شماره موبایل': '09123456789',
            'دپارتمان': 'فروش'
        }]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'پرسنل');
        XLSX.writeFile(wb, 'نمونه_فایل_پرسنل.xlsx');
    };

    const handleExcelUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setIsUploading(true);
        const reader = new FileReader();
        reader.onload = async (evt) => {
            try {
                const bstr = evt.target.result;
                const wb = XLSX.read(bstr, { type: 'binary' });
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];
                const data = XLSX.utils.sheet_to_json(ws);

                if (data.length === 0) throw new Error('فایل اکسل خالی است.');

                const cleanEmployees = data.map(row => ({
                    full_name: row['نام و نام خانوادگی'] || row['نام'] || row['full_name'] || '',
                    personnel_code: String(row['کد پرسنلی'] || row['personnel_code'] || '').trim() || null,
                    national_code: String(row['کد ملی'] || row['national_code'] || '').trim() || null,
                    mobile_phone: String(row['شماره موبایل'] || row['موبایل'] || row['mobile_phone'] || '').trim(),
                    department: row['دپارتمان'] || row['department'] || null,
                })).filter(emp => emp.full_name && emp.mobile_phone);

                if (cleanEmployees.length === 0) throw new Error('هیچ ردیف معتبری (دارای نام و موبایل) در فایل یافت نشد.');

                const { error } = await apiClient.POST('/api/v1/company/employees/bulk', {
                    body: { employees: cleanEmployees }
                });

                if (error) throw new Error(error.detail || 'خطا در ثبت گروهی از اکسل');

                alert(`${cleanEmployees.length} کارمند با موفقیت از فایل اکسل اضافه شدند.`);
                loadEmployees();
            } catch (err) {
                alert('خطا در پردازش فایل: ' + err.message);
            } finally {
                setIsUploading(false);
                e.target.value = null;
            }
        };
        reader.readAsBinaryString(file);
    };

    // === توابع ویرایش ===
    const openEditModal = (emp) => {
        setEditingEmployee(emp);
        setEditFormData({
            full_name: emp.full_name || '',
            personnel_code: emp.personnel_code || '',
            national_code: emp.national_code || '',
            mobile_phone: emp.mobile_phone || '',
            department: emp.department || '',
            is_active: emp.is_active !== false,
        });
        setEditError('');
    };

    const closeEditModal = () => {
        if (savingEdit) return;
        setEditingEmployee(null);
        setEditError('');
    };

    const handleEditChange = (field, value) => {
        setEditFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleUpdate = async () => {
        if (!editingEmployee) return;
        setEditError('');

        if (!editFormData.full_name.trim() || editFormData.full_name.trim().length < 2) {
            setEditError('نام و نام خانوادگی باید حداقل ۲ کاراکتر باشد.');
            return;
        }
        if (!editFormData.mobile_phone.trim() || editFormData.mobile_phone.trim().length < 10) {
            setEditError('شماره موبایل معتبر وارد کنید (حداقل ۱۰ رقم).');
            return;
        }

        const payload = {
            full_name: editFormData.full_name.trim(),
            personnel_code: editFormData.personnel_code.trim() || null,
            national_code: editFormData.national_code.trim() || null,
            mobile_phone: editFormData.mobile_phone.trim(),
            department: editFormData.department.trim() || null,
            is_active: editFormData.is_active,
        };

        setSavingEdit(true);
        try {
            const { error } = await apiClient.PATCH(`/api/v1/company/employees/${editingEmployee.id}`, {
                body: payload,
            });
            if (error) {
                const msg = Array.isArray(error.detail) ? error.detail.map(d => d.msg || d).join(' | ') : (error.detail || 'خطا در ویرایش');
                throw new Error(msg);
            }

            alert('اطلاعات کارمند با موفقیت به‌روزرسانی شد.');
            closeEditModal();
            loadEmployees();
        } catch (e) {
            setEditError(e.message);
        } finally {
            setSavingEdit(false);
        }
    };

    const handleToggleActive = async (emp) => {
        const actionText = emp.is_active ? 'غیرفعال' : 'فعال';
        if (!window.confirm(`آیا می‌خواهید «${emp.full_name}» را ${actionText} کنید؟`)) return;

        setTogglingId(emp.id);
        try {
            const { error } = await apiClient.PATCH(`/api/v1/company/employees/${emp.id}`, {
                body: { is_active: !emp.is_active },
            });
            if (error) throw new Error(error.detail || 'خطا در تغییر وضعیت');
            loadEmployees();
        } catch (e) {
            alert(e.message);
        } finally {
            setTogglingId(null);
        }
    };

    return (
        <div className="company-employees-page" style={{ direction: 'rtl', display: 'flex', flexDirection: 'column', gap: '1.5rem', width: '100%' }}>

            {/* هدر صفحه: عنوان و دکمه‌ها */}
            <div className="company-employees-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fff', padding: '1rem 1.5rem', borderRadius: '12px', border: '1px solid #e5e7eb', flexWrap: 'wrap', gap: '1rem' }}>
                <h2 style={{ margin: 0, fontSize: '1.2rem', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '0.5rem', whiteSpace: 'nowrap' }}>
                    <Users size={22} color="#2563eb" />
                    مدیریت پرسنل
                </h2>

                <div className="company-employees-actions" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <button
                        onClick={downloadTemplate}
                        style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1', padding: '0.5rem 1rem', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.85rem', whiteSpace: 'nowrap' }}
                        title="دانلود فایل نمونه برای ورود داده‌ها"
                    >
                        <Download size={16} /> فایل نمونه
                    </button>

                    <div style={{ position: 'relative' }}>
                        <input type="file" accept=".xlsx, .xls, .csv" onChange={handleExcelUpload} style={{ display: 'none' }} id="excel-upload" />
                        <label
                            htmlFor="excel-upload"
                            style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: '#8b5cf6', color: '#fff', border: 'none', padding: '0.5rem 1rem', borderRadius: '8px', cursor: isUploading ? 'not-allowed' : 'pointer', fontWeight: 'bold', fontSize: '0.85rem', opacity: isUploading ? 0.7 : 1, whiteSpace: 'nowrap' }}
                        >
                            <Upload size={16} /> {isUploading ? 'در حال آپلود...' : 'ورود از اکسل'}
                        </label>
                    </div>

                    <button
                        onClick={() => navigate('/company/employees/add')}
                        style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: '#2563eb', color: '#fff', border: 'none', padding: '0.5rem 1rem', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.85rem', whiteSpace: 'nowrap' }}
                    >
                        <Plus size={16} /> افزودن کارمند
                    </button>
                </div>
            </div>

            {/* جدول لیست پرسنل */}
            <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden', width: '100%' }}>
                <div className="company-table-scroll" style={{ overflowX: 'auto' }}>
                    <table className="company-table" style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'center', whiteSpace: 'nowrap' }}>
                        <thead style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                        <tr>
                            <th style={{ padding: '1rem', color: '#475569', fontSize: '0.9rem', fontWeight: 'bold' }}>نام</th>
                            <th style={{ padding: '1rem', color: '#475569', fontSize: '0.9rem', fontWeight: 'bold' }}>کد پرسنلی</th>
                            <th style={{ padding: '1rem', color: '#475569', fontSize: '0.9rem', fontWeight: 'bold' }}>کد ملی</th>
                            <th style={{ padding: '1rem', color: '#475569', fontSize: '0.9rem', fontWeight: 'bold' }}>موبایل</th>
                            <th style={{ padding: '1rem', color: '#475569', fontSize: '0.9rem', fontWeight: 'bold' }}>دپارتمان</th>
                            <th style={{ padding: '1rem', color: '#475569', fontSize: '0.9rem', fontWeight: 'bold' }}>وضعیت</th>
                            <th style={{ padding: '1rem', color: '#475569', fontSize: '0.9rem', fontWeight: 'bold', width: '180px' }}>عملیات</th>
                        </tr>
                        </thead>
                        <tbody>
                        {employees.length === 0 ? (
                            <tr><td colSpan="7" style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>کارمندی یافت نشد</td></tr>
                        ) : (
                            employees.map(emp => (
                                <tr key={emp.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                    <td style={{ padding: '1rem', fontWeight: 'bold', color: '#1e293b' }}>{emp.full_name}</td>
                                    <td style={{ padding: '1rem', color: '#475569' }}>{emp.personnel_code || '-'}</td>
                                    <td style={{ padding: '1rem', color: '#475569' }}>{emp.national_code || '-'}</td>
                                    <td style={{ padding: '1rem', color: '#475569', direction: 'ltr' }}>{emp.mobile_phone}</td>
                                    <td style={{ padding: '1rem', color: '#475569' }}>{emp.department || '-'}</td>
                                    <td style={{ padding: '1rem' }}>
                                        <span style={{ padding: '0.2rem 0.6rem', borderRadius: '999px', fontSize: '0.8rem', background: emp.is_active ? '#dcfce7' : '#fee2e2', color: emp.is_active ? '#15803d' : '#b91c1c', fontWeight: 'bold' }}>
                                            {emp.is_active ? 'فعال' : 'غیرفعال'}
                                        </span>
                                    </td>
                                    <td style={{ padding: '1rem' }}>
                                        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                                            <button type="button" onClick={() => openEditModal(emp)} style={{ background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', padding: '0.4rem 0.6rem', borderRadius: '6px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.8rem', fontWeight: 'bold' }}>
                                                <Pencil size={14} /> ویرایش
                                            </button>
                                            <button type="button" onClick={() => handleToggleActive(emp)} disabled={togglingId === emp.id} style={{ background: emp.is_active ? '#fef2f2' : '#dcfce7', color: emp.is_active ? '#ef4444' : '#15803d', border: `1px solid ${emp.is_active ? '#fecaca' : '#86efac'}`, padding: '0.4rem 0.6rem', borderRadius: '6px', cursor: togglingId === emp.id ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.8rem', fontWeight: 'bold', opacity: togglingId === emp.id ? 0.5 : 1 }}>
                                                {emp.is_active ? <PowerOff size={14} /> : <Power size={14} />}
                                                {togglingId === emp.id ? '...' : (emp.is_active ? 'غیرفعال' : 'فعال')}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* مودال ویرایش */}
            {editingEmployee && (
                <div className="company-modal-backdrop" onClick={closeEditModal} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, direction: 'rtl' }}>
                    <div className="company-modal" onClick={(e) => e.stopPropagation()} style={{ background: '#fff', width: '92%', maxWidth: '560px', maxHeight: '92vh', overflowY: 'auto', borderRadius: '12px', padding: '1.5rem', position: 'relative' }}>
                        <button type="button" onClick={closeEditModal} disabled={savingEdit} style={{ position: 'absolute', top: '1rem', left: '1rem', background: 'none', border: 'none', cursor: savingEdit ? 'not-allowed' : 'pointer', opacity: savingEdit ? 0.4 : 1 }}>
                            <X size={20} color="#ef4444" />
                        </button>

                        <h3 style={{ marginTop: 0, marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1.15rem' }}>
                            <User size={20} color="#2563eb" /> ویرایش اطلاعات کارمند
                        </h3>

                        {editError && <div style={{ background: '#fef2f2', color: '#b91c1c', padding: '0.75rem', borderRadius: '8px', marginBottom: '1rem', fontSize: '0.9rem', border: '1px solid #fecaca' }}>{editError}</div>}

                        <div style={{ display: 'grid', gap: '0.85rem' }}>
                            <div>
                                <label style={{ display: 'block', marginBottom: '0.3rem', fontSize: '0.85rem', fontWeight: 'bold', color: '#334155' }}>نام و نام خانوادگی *</label>
                                <input type="text" value={editFormData.full_name} onChange={(e) => handleEditChange('full_name', e.target.value)} style={{ width: '100%', padding: '0.55rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.95rem' }} />
                            </div>

                            <div className="company-employee-code-fields" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '0.3rem', fontSize: '0.85rem', fontWeight: 'bold', color: '#334155' }}>کد پرسنلی</label>
                                    <input type="text" value={editFormData.personnel_code} onChange={(e) => handleEditChange('personnel_code', e.target.value)} placeholder="اختیاری" style={{ width: '100%', padding: '0.55rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.95rem' }} />
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '0.3rem', fontSize: '0.85rem', fontWeight: 'bold', color: '#334155' }}>کد ملی</label>
                                    <input type="text" value={editFormData.national_code} onChange={(e) => handleEditChange('national_code', e.target.value)} placeholder="اختیاری" maxLength={10} style={{ width: '100%', padding: '0.55rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.95rem' }} />
                                </div>
                            </div>

                            <div>
                                <label style={{ display: 'block', marginBottom: '0.3rem', fontSize: '0.85rem', fontWeight: 'bold', color: '#334155' }}>شماره موبایل *</label>
                                <input type="text" value={editFormData.mobile_phone} onChange={(e) => handleEditChange('mobile_phone', e.target.value)} style={{ width: '100%', padding: '0.55rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.95rem', direction: 'ltr', textAlign: 'right' }} />
                                <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem' }}>با تغییر موبایل، لینک کاربر (در صورت وجود) حفظ می‌شود اما مالکیت بررسی مجدد نمی‌شود.</div>
                            </div>

                            <div>
                                <label style={{ display: 'block', marginBottom: '0.3rem', fontSize: '0.85rem', fontWeight: 'bold', color: '#334155' }}>دپارتمان</label>
                                <input type="text" value={editFormData.department} onChange={(e) => handleEditChange('department', e.target.value)} placeholder="اختیاری" style={{ width: '100%', padding: '0.55rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.95rem' }} />
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', background: '#f8fafc', padding: '0.7rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                <input type="checkbox" id="is_active_checkbox" checked={editFormData.is_active} onChange={(e) => handleEditChange('is_active', e.target.checked)} style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
                                <label htmlFor="is_active_checkbox" style={{ cursor: 'pointer', fontSize: '0.9rem', fontWeight: 'bold', color: '#334155' }}>کارمند فعال است (در لیست پرسنل قابل استفاده)</label>
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                            <button type="button" onClick={closeEditModal} disabled={savingEdit} style={{ background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1', padding: '0.6rem 1.25rem', borderRadius: '8px', cursor: savingEdit ? 'not-allowed' : 'pointer', opacity: savingEdit ? 0.5 : 1 }}>انصراف</button>
                            <button type="button" onClick={handleUpdate} disabled={savingEdit} className="btn-primary" style={{ padding: '0.6rem 1.5rem', display: 'inline-flex', alignItems: 'center', gap: '0.4rem', cursor: savingEdit ? 'not-allowed' : 'pointer', opacity: savingEdit ? 0.6 : 1 }}>
                                <Save size={16} /> {savingEdit ? 'در حال ذخیره...' : 'ذخیره تغییرات'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}